import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import ConfirmDialog from "components/Modals/ConfirmDialog";
import UploadFile from "components/Input/UploadFile";
import { P } from "const/permissions";
import { usePermission } from "hooks/usePermissions";
import { useState } from "react";
import { toast } from "react-toastify";
import PaymentReceiptChips from "components/Payments/PaymentReceiptChips";
import { attachPayoutReceipts, listPayouts, reversePayout } from "../referralPartnersApi";

const money = (value) =>
  `$${Number(value || 0).toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/**
 * Payouts already sent, and the way back. Reversing one returns its entries to approved and
 * unlocks the referral amount in the account, which is the only way to correct a paid figure.
 */
const PayoutHistoryPanel = ({ partnerId, onChanged }) => {
  const queryClient = useQueryClient();
  const canPay = usePermission(P.REFERRAL.PAYOUT_CREATE);
  const [reversing, setReversing] = useState(null);
  const [reason, setReason] = useState("");
  const [attaching, setAttaching] = useState(null);
  const [upload, setUpload] = useState({ receipts: [] });

  const [page, setPage] = useState(0);
  const pageSize = 20;

  const { data, isLoading, refetch } = useQuery(
    ["referral-payouts", partnerId, page],
    () => listPayouts(partnerId, { page, size: pageSize }),
    { keepPreviousData: true, refetchOnMount: true }
  );
  const totalPages = data?.totalPages ?? 0;

  const reverseMutation = useMutation(
    ({ payoutId, note }) => reversePayout(partnerId, payoutId, note),
    {
      onSuccess: () => {
        toast.success("Payout marked unpaid");
        setReversing(null);
        setReason("");
        refetch();
        queryClient.invalidateQueries(["referral-commissions", partnerId]);
        onChanged?.();
      },
      onError: (err) =>
        toast.error(
          err?.response?.data?.errorMessage ||
            err?.response?.data?.message ||
            "Could not mark that payout unpaid"
        ),
    }
  );

  const attachMutation = useMutation(
    ({ payoutId, receiptDocuments }) => attachPayoutReceipts(partnerId, payoutId, receiptDocuments),
    {
      onSuccess: () => {
        toast.success("Receipt attached");
        setAttaching(null);
        setUpload({ receipts: [] });
        refetch();
      },
      onError: (err) =>
        toast.error(
          err?.response?.data?.errorMessage ||
            err?.response?.data?.message ||
            "Could not attach that receipt"
        ),
    }
  );

  const payouts = data?.content || [];
  if (!isLoading && payouts.length === 0) return null;

  return (
    <div className="bg-app-surface rounded shadow p-4 mt-4">
      <Typography variant="h6" className="mb-2">
        Payouts
      </Typography>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-app-text-muted border-b border-app-border">
              <th className="py-2 pr-3">Reference</th>
              <th className="py-2 pr-3">Paid on</th>
              <th className="py-2 pr-3 text-right">Amount</th>
              <th className="py-2 pr-3">Entries</th>
              <th className="py-2 pr-3">Receipts</th>
              <th className="py-2 pr-3" />
            </tr>
          </thead>
          <tbody>
            {payouts.map((payout) => (
              <tr key={payout.id} className="border-b border-app-border last:border-0">
                <td className="py-2 pr-3 font-medium">{payout.reference || "—"}</td>
                <td className="py-2 pr-3">{payout.paidDate || "—"}</td>
                <td className="py-2 pr-3 text-right tabular-nums">{money(payout.amount)}</td>
                <td className="py-2 pr-3 text-app-text-muted">{payout.rowCount}</td>
                <td className="py-2 pr-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <PaymentReceiptChips urls={payout.receiptDocuments || []} sx={{}} />
                    {(payout.receiptDocuments || []).length === 0 ? (
                      <span className="text-xs text-app-text-muted">None</span>
                    ) : null}
                    {canPay && !payout.reversedAt ? (
                      <Button
                        size="small"
                        onClick={() => {
                          setAttaching(payout);
                          setUpload({ receipts: [] });
                        }}
                      >
                        Add
                      </Button>
                    ) : null}
                  </div>
                </td>
                <td className="py-2 pr-3 text-right">
                  {payout.reversedAt ? (
                    <span className="text-xs text-app-text-muted">
                      Marked unpaid{payout.reversalReason ? `: ${payout.reversalReason}` : ""}
                    </span>
                  ) : canPay ? (
                    <Button size="small" color="warning" onClick={() => setReversing(payout)}>
                      Mark unpaid
                    </Button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {totalPages > 1 ? (
        <div className="flex items-center justify-between gap-3 pt-3 mt-3 border-t border-app-border">
          <Button
            size="small"
            disabled={page === 0}
            onClick={() => setPage((p) => Math.max(p - 1, 0))}
          >
            Previous
          </Button>
          <Typography variant="body2" color="text.secondary">
            Page {page + 1} of {totalPages}
          </Typography>
          <Button
            size="small"
            disabled={page + 1 >= totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </Button>
        </div>
      ) : null}

      <Dialog open={Boolean(attaching)} onClose={() => setAttaching(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Attach a receipt to {attaching?.reference || "this payout"}</DialogTitle>
        <DialogContent dividers>
          <UploadFile
            data={upload}
            setData={setUpload}
            imageKey="receipts"
            label="Transfer receipt / proof"
            type="multiple"
            newDesign
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAttaching(null)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={(upload.receipts || []).length === 0 || attachMutation.isLoading}
            onClick={() =>
              attachMutation.mutate({
                payoutId: attaching.id,
                receiptDocuments: (upload.receipts || []).filter(Boolean),
              })
            }
          >
            {attachMutation.isLoading ? "Attaching…" : "Attach"}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(reversing)}
        title="Mark this payout as unpaid?"
        message={
          <div>
            <p className="m-0 mb-3 text-sm">
              {reversing?.reference || "This payout"} of {money(reversing?.amount)} goes back to
              approved. Its referral amounts become editable in the account again.
            </p>
            <TextField
              fullWidth
              size="small"
              label="Reason (optional)"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
        }
        confirmLabel="Mark unpaid"
        loading={reverseMutation.isLoading}
        onConfirm={() => reverseMutation.mutate({ payoutId: reversing.id, note: reason })}
        onCancel={() => {
          setReversing(null);
          setReason("");
        }}
      />
    </div>
  );
};

export default PayoutHistoryPanel;
